import React, { useState, useEffect } from 'react';
import { Textarea } from '../../ui/textarea';
import { useLazyCommand } from '@soap/modules';
import { Loader2, Edit2 } from 'lucide-react';
import type { TopicInlineEditTarget } from './InlineTextFieldEditor';

interface InlineListEditorProps {
  value: string[] | null | undefined;
  topicId: string;
  studentId: string | null;
  editTarget?: TopicInlineEditTarget;
  fieldName: string;
  label: string;
  onUpdate?: (newValue: string[] | null) => void;
}

export function InlineListEditor({
  value,
  topicId,
  studentId,
  editTarget = 'student',
  fieldName,
  label,
  onUpdate
}: InlineListEditorProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState((value || []).join('\n'));
  const [isSaving, setIsSaving] = useState(false);
  const [updateStudentCatalogTopicFields] = useLazyCommand("C299v1_UpdateCustomTopicFields");
  const [updateTenantCatalogTopicFields] = useLazyCommand("C320v1_UpdateTenantCustomCatalogTopicFields");

  const canEdit =
    editTarget !== 'none' &&
    !!topicId &&
    (editTarget === 'tenant' || (!!studentId && editTarget === 'student'));

  useEffect(() => {
    setEditValue((value || []).join('\n'));
  }, [value]);

  const handleSave = async () => {
    if (!topicId || !canEdit) {
      setEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      const listValue = editValue
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      const pipeValue = listValue.length > 0 ? listValue.join('|') : null;

      if (editTarget === 'tenant') {
        await updateTenantCatalogTopicFields({
          $type: "C320v1_UpdateTenantCustomCatalogTopicFields",
          C320_TopicId: topicId,
          [`C320_${fieldName}`]: pipeValue
        });
      } else if (editTarget === 'student' && studentId) {
        await updateStudentCatalogTopicFields({
          $type: "C299v1_UpdateCustomTopicFields",
          C299_StudentId: studentId,
          C299_TopicId: topicId,
          [`C299_${fieldName}`]: pipeValue
        });
      } else {
        setEditing(false);
        return;
      }

      if (onUpdate) {
        onUpdate(listValue.length > 0 ? listValue : null);
      }

      setEditing(false);
    } catch (error) {
      console.error(`Failed to update topic ${fieldName}:`, error);
      setEditValue((value || []).join('\n'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEditValue((value || []).join('\n'));
      setEditing(false);
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSave();
    }
  };

  if (editing) {
    return (
      <div className="space-y-2">
        <Textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          rows={Math.max(3, (value || []).length + 2)}
          className="text-sm font-mono"
          autoFocus
          placeholder={`Enter ${label.toLowerCase()}, one per line...`}
        />
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>One item per line. Press Ctrl+Enter to save, Esc to cancel</span>
          {isSaving && <Loader2 className="h-3 w-3 animate-spin" />}
        </div>
      </div>
    );
  }

  if (!value || value.length === 0) {
    if (!canEdit) {
      return <span className="text-sm text-gray-500">—</span>;
    }
    return (
      <button
        onClick={() => setEditing(true)}
        className="flex items-center gap-1.5 px-2 py-1 hover:bg-action-blue/10 hover:border-action-blue/40 border border-transparent rounded text-sm font-semibold text-gray-600 transition-colors group"
        disabled={isSaving}
        title={`Click to add ${label.toLowerCase()}`}
      >
        <span>Add {label}</span>
        <Edit2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-action-blue transition-colors" />
      </button>
    );
  }

  return (
    <div className="group">
      <ul className="list-disc list-inside space-y-1">
        {value.map((item: string, idx: number) => (
          <li key={idx} className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
            {item}
          </li>
        ))}
      </ul>
      {canEdit && (
        <button
          onClick={() => setEditing(true)}
          className="mt-2 flex items-center gap-1.5 px-2 py-1 hover:bg-action-blue/10 hover:border-action-blue/40 border border-transparent rounded text-sm font-semibold text-gray-600 transition-colors group"
          disabled={isSaving}
          title={`Click to edit ${label.toLowerCase()}`}
        >
          <span>Edit {label}</span>
          <Edit2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-action-blue transition-colors" />
        </button>
      )}
    </div>
  );
}
